import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Tab } from 'semantic-ui-react';
import ReportContent from './ReportContent';

import styles from './Report.module.scss';

const Report = React.memo(
  ({
    isEditable,
    dailyReportWk,
    monthlyReportWk,
    yearlyReportWk,
    onChangeDaily,
    onChangeDailyInterval,
    onChangeMonthly,
    onChangeYearly,
    onFetchReports,
  }) => {
    const [t] = useTranslation();
    const [currentDailyPage, setDailyPage] = useState(1);
    const [currentMonthlyPage, setMonthlyPage] = useState(1);
    const [currentYearlyPage, setYearlyPage] = useState(1);
    const [dataDaily, setDailyData] = useState([]);
    const [dataMonthly, setMonthlyData] = useState([]);
    const [dataYearly, setYearlyData] = useState([]);

    const savedDailyReport = useRef(dailyReportWk);
    const savedDataDaily = useRef(dataDaily);
    const savedMonthlyReport = useRef(monthlyReportWk);
    const savedDataMonthly = useRef(dataMonthly);
    const savedYearlyReport = useRef(yearlyReportWk);
    const savedDataYearly = useRef(dataYearly);

    console.log('********************** Report render');

    const resultsPerPage = 15;
    let totalDailyPages = 0;
    let totalMonthlyPages = 0;
    let totalYearlyPages = 0;
    if (dailyReportWk.energy) {
      totalDailyPages = Math.ceil(dailyReportWk.energy.length / resultsPerPage);
    }
    if (monthlyReportWk.energy) {
      totalMonthlyPages = Math.ceil(monthlyReportWk.energy.length / resultsPerPage);
    }
    if (yearlyReportWk.energy) {
      totalYearlyPages = Math.ceil(yearlyReportWk.energy.length / resultsPerPage);
    }

    const onDailyPageChange = useCallback((e, { activePage }) => {
      setDailyPage(activePage);
    }, []);
    const onMonthlyPageChange = useCallback((e, { activePage }) => {
      setMonthlyPage(activePage);
    }, []);
    const onYearlyPageChange = useCallback((e, { activePage }) => {
      setYearlyPage(activePage);
    }, []);

    useEffect(() => {
      if (dailyReportWk.energy) {
        setDailyData(
          dailyReportWk.energy.slice(
            (currentDailyPage - 1) * resultsPerPage,
            currentDailyPage * resultsPerPage,
          ),
        );
      }
      if (monthlyReportWk.energy) {
        setMonthlyData(
          monthlyReportWk.energy.slice(
            (currentMonthlyPage - 1) * resultsPerPage,
            currentMonthlyPage * resultsPerPage,
          ),
        );
      }
      if (yearlyReportWk.energy) {
        setYearlyData(
          yearlyReportWk.energy.slice(
            (currentYearlyPage - 1) * resultsPerPage,
            currentYearlyPage * resultsPerPage,
          ),
        );
      }
    }, [
      dailyReportWk,
      monthlyReportWk,
      yearlyReportWk,
      currentDailyPage,
      currentMonthlyPage,
      currentYearlyPage,
    ]);

    /* prevent re-rendering */
    if (JSON.stringify(savedDailyReport.current) !== JSON.stringify(dailyReportWk)) {
      savedDailyReport.current = dailyReportWk;
    }

    if (JSON.stringify(savedDataDaily.current) !== JSON.stringify(dataDaily)) {
      savedDataDaily.current = dataDaily;
    }

    if (JSON.stringify(savedMonthlyReport.current) !== JSON.stringify(monthlyReportWk)) {
      savedMonthlyReport.current = monthlyReportWk;
    }

    if (JSON.stringify(savedDataMonthly.current) !== JSON.stringify(dataMonthly)) {
      savedDataMonthly.current = dataMonthly;
    }

    if (JSON.stringify(savedYearlyReport.current) !== JSON.stringify(yearlyReportWk)) {
      savedYearlyReport.current = yearlyReportWk;
    }

    if (JSON.stringify(savedDataYearly.current) !== JSON.stringify(dataYearly)) {
      savedDataYearly.current = dataYearly;
    }

    const panes = [
      {
        menuItem: t('common.reports.day'),
        render: () => (
          <Tab.Pane>
            <ReportContent
              isEditable={isEditable}
              type="daily"
              reports={savedDailyReport.current}
              currentPageEnergy={savedDataDaily.current}
              totalPages={totalDailyPages}
              currentPage={currentDailyPage}
              onPageChange={onDailyPageChange}
              onChangeDate={onChangeDaily}
              onChangeDateInterval={onChangeDailyInterval}
              onFetchReports={onFetchReports}
            />
          </Tab.Pane>
        ),
      },
      {
        menuItem: t('common.reports.month'),
        render: () => (
          <Tab.Pane>
            <ReportContent
              isEditable={isEditable}
              type="monthly"
              reports={savedMonthlyReport.current}
              currentPageEnergy={savedDataMonthly.current}
              totalPages={totalMonthlyPages}
              currentPage={currentMonthlyPage}
              onPageChange={onMonthlyPageChange}
              onChangeDate={onChangeMonthly}
              onFetchReports={onFetchReports}
            />
          </Tab.Pane>
        ),
      },
      {
        menuItem: t('common.reports.year'),
        render: () => (
          <Tab.Pane>
            <ReportContent
              isEditable={isEditable}
              type="yearly"
              reports={savedYearlyReport.current}
              currentPageEnergy={savedDataYearly.current}
              totalPages={totalYearlyPages}
              currentPage={currentYearlyPage}
              onPageChange={onYearlyPageChange}
              onChangeDate={onChangeYearly}
              onFetchReports={onFetchReports}
            />
          </Tab.Pane>
        ),
      },
    ];

    return (
      <div className={styles.devicesWrapper}>
        <h2 className="page-title">{t('common.report')}</h2>
        <Tab panes={panes} />
      </div>
    );
  },
);

Report.propTypes = {
  dailyReportWk: PropTypes.objectOf(PropTypes.any).isRequired,
  monthlyReportWk: PropTypes.objectOf(PropTypes.any).isRequired,
  yearlyReportWk: PropTypes.objectOf(PropTypes.any).isRequired,
  isEditable: PropTypes.bool.isRequired,
  onChangeDaily: PropTypes.func.isRequired,
  onChangeDailyInterval: PropTypes.func.isRequired,
  onChangeMonthly: PropTypes.func.isRequired,
  onChangeYearly: PropTypes.func.isRequired,
  onFetchReports: PropTypes.func.isRequired,
};

export default Report;
